<template>
  <div class="card">
    <vue-easy-lightbox
        moveDisabled
        :visible="isLightboxVisible"
        :imgs="lightBoxImageUrl"
        @hide="isLightboxVisible = false"
    ></vue-easy-lightbox>
    <div class="card-body">
      <!-- #region Request and Refresh buttons -->
      <div class="d-flex align-items-center py-2">
        <div>
          <h5>{{ eloadB2BRequestsTotal }} Record(s)</h5>
        </div>
        <div class="ms-auto">
          <router-link :to="`form/0`" v-if="userAccess.access.create">
            <button class="btn btn-primary me-2 mb-2"><i class="uil uil-plus me-1"></i>Request Fund</button>
          </router-link>
          <button class="btn btn-secondary mb-2" @click="getRequests()">
            <feather type="refresh-ccw" size="16" class="icon nav-icon"></feather>&nbsp;&nbsp; Refresh
          </button>
        </div>
      </div>
      <!-- #endregion Request and Refresh buttons -->

      <!-- #region Search and Filter -->
      <div class="row p-2">
        <div class="col-md-4 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Search Keyword">
              <form class="d-flex" @submit.prevent="">
                <b-form-group id="input-group-3" label-for="search-field">
                  <b-form-select
                    class="form-select rounded-0 rounded-start shadow-none"
                    id="search-field"
                    v-model="search.field"
                    :options="searchFieldOptions"
                  ></b-form-select>
                </b-form-group>

                <b-input-group>
                  <b-form-input class="rounded-0 rounded-end" v-model="search.keyword"></b-form-input>
                </b-input-group>
              </form>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Status">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.status"
                :options="statusOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Requested Date" class="me-2 w-daterange">
              <flat-pickr
                v-model="search.dateRange"
                :config="rangeOptions"
                placeholder="Select a date range"
                class="form-control"
              ></flat-pickr>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 d-flex">
          <div class="me-2">
            <b-button variant="primary" class="h-fit d-flex align-items-center justify-content-center me-2 w-fit" @click="getRequests">
              <i class="bx bx-search me-1 mt-1"></i>Search
            </b-button>
          </div>
          <div class="">
            <b-button
              variant="primary"
              class="h-fit me-2 d-flex align-items-center justify-content-center w-fit"
              @click="
                isSearching = true;
                onExport();
              "
            >
              <i class="uil uil-export me-1"></i>
              Export
            </b-button>
          </div>
        </div>
      </div>
      <!-- #endregion Search and Filter -->

      <!-- #region Table -->
      <div class="table-responsive">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="eloadB2BRequests"
            :fields="fields"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="orderBy"
            :sort-desc.sync="sortDesc"
            :filter-included-fields="filterOn"
            class="table-check"
            @sort-changed="sortList"
            tbody-tr-class="align-middle"
          >
            <template #cell(action)="data">
              <!-- TODO: Add checking if approver -->

              <b2b-action
               view = "list"
               :requestData = data.item
               :userAccess = userAccess.access
               @reload = "getRequests()"
              />
            </template>

            <template #cell(id)="data">
              <router-link :to="`details/${data.item.reference_number}`">
                {{ data.item.id | addPadding }}
              </router-link>
              <div>{{ data.item.created_at | formatDate }}</div>
            </template>

            <template #cell(reference_number)="data">
              <div>{{ data.item.reference_number }}</div>
              <div>{{ data.item.sender_bank_name }}</div>
            </template>

            <template #cell(user_id)="data">
              <div>{{ data.item.user_id }}</div>
              <div>{{ data.item.name }}</div>
            </template>

            <template #cell(status)="data">
              <div
                :class="{
                  'text-success': data.item.status == 'approved',
                  'requesting-color-code': data.item.status == 'requesting',
                  'text-muted': data.item.status == 'rejected',
                }"
              >
                {{ setStatus(data.item.status) }}
              </div>
            </template>

            <template #cell(deposit_ss_url)="data">
              <b-img @click="viewImage(data.item.deposit_ss_url)"
                :src="data.item.deposit_ss_url"
                @error="(e) => (e.target.src = require('@/assets/images/img-placeholder.png'))"
                alt=""
                thumbnail
                height="50"
                width="50"
                class="c-pointer"
              >
              </b-img>
            </template>

            <template #cell(business_name)="data">
              <div>{{ data.item.business_name }}</div>
            </template>

            <template #cell(mobile_number)="data">
              <div>{{ data.item.mobile_number }}</div>
            </template>

            <template #cell(deposit_date)="data">
              <div>{{ data.item.deposit_date | formatDateOnly }}</div>
            </template>

            <template #cell(paid_amount)="data">
              <div>PHP {{ data.item.paid_amount | numberWithCommas }}</div>
            </template>

            <template #cell(topup_amount)="data">
              <div>PHP {{ data.item.topup_amount | numberWithCommas }}</div>
            </template>

            <template #cell(requested_by)="data">
              <div>{{ data.item.requested_by }}</div>
            </template>

            <template #cell(approved_by)="data">
              <div>{{ data.item.approved_by }}</div>
            </template>

            <template #cell(approved_at)="data">
              <div>{{ data.item.approved_at | formatDate }}</div>
            </template>

            <!-- TODO: PEDDLR-5804 Add formatting of remarks based on status -->
            <template #cell(remarks)="data">
              <div v-if="data.item.status == 'approved'">
                <a :href="data.item.remarks" target="_blank" class="text-decoration-underline" style="color: #3967B8;">Proof of Confirmation</a>
              </div>
              <div v-else v-bind:title='data.item.remarks'>{{ data.item.remarks |truncator(20,'...') }}</div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row g-0 text-center" v-if="eloadB2BRequestsTotal == 0">
        <span>No data to display.</span>
      </div>
      <!-- #endregion Table -->

      <!-- #region Pagination -->
      <div class="row g-0 text-center text-sm-start" v-if="eloadB2BRequestsTotal != 0 && eloadB2BRequestsTotal > 10">
        <div class="col-sm-12">
          <div class="row">
            <div class="col">
              <div class="form-group row pull-left">
                <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
                <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                    <b-form-select
                      class="form-select shadow-none w-fit"
                      id="display-record"
                      v-model="perPage"
                      :options="pageOptions"
                      @change="getRequests()"
                    ></b-form-select>
                  </b-form-group>
                </div>
              </div>
              <div class="dataTables_paginate paging_simple_numbers float-end" v-if="eloadB2BRequestsTotal > perPage">
                <b-pagination
                  class="pagination-rounded"
                  v-model="currentPage"
                  @input="getRequests()"
                  :total-rows="eloadB2BRequestsTotal"
                  :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- #endregion Pagination -->
    </div>
  </div>
</template>

<script>
import { layoutMethods, eloadB2BMethods, eloadB2BComputed, exportMethods } from "@/state/helpers";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";
import { getModuleAccess } from '@/router/helper.js'
import b2bAction from '@/views/pages/eloadB2B/action.vue';

export default {
  page: {
    title: "ELoad B2B Requests Dashboard",
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      filter: null,
      filterOn: [],
      orderBy: "ebr.id",
      sort: "DESC",
      sortDesc: false,
      submitted: false,
      search: {
        keyword: "",
        field: "",
        status: "",
        dateRange: null,
      },
      rangeOptions: {
        mode: "range",
      },
      searchFieldOptions: [
        { text: "Search All", value: "" },
        { text: "Request ID", value: "ebr.id" },
        { text: "Reference Number", value: "ebr.reference_number" },
        { text: "User ID", value: "ebr.user_id" },
        { text: "Name", value: "u.name" },
        { text: "Business Name", value: "ebr.business_name" },
        { text: "Mobile Number", value: "u.mobile_number" },
        { text: "Requested By", value: "ebr.requested_by" },
        { text: "Approved By", value: "ebr.approved_by" },
      ],
      statusOptions: [
        { text: "All", value: "" },
        { text: "Requesting", value: "requesting" },
        { text: "Approved", value: "approved" },
        { text: "Rejected", value: "rejected" },
      ],
      fields: [
        { key: "action", sortable: false },
        { key: "id", label: "Request ID & Date", sortable: true },
        { key: "reference_number", label: "Payment Reference Number", sortable: true },
        { key: "user_id", label: "User ID & Name", sortable: true },
        { key: "status", sortable: true },
        { key: "deposit_ss_url", label: "Deposit Screenshot", sortable: true },
        { key: "business_name", sortable: true },
        { key: "mobile_number", sortable: true },
        { key: "deposit_date", sortable: true },
        { key: "paid_amount", sortable: true },
        { key: "topup_amount", sortable: true },
        { key: "requested_by", sortable: true },
        { key: "approved_by", label: "Approved By Finance", sortable: true },
        { key: "approved_at", sortable: true },
        { key: "remarks", sortable: false },
      ],
      userAccess: {
        access: {
          create: false,
          read: false,
          update: false,
          delete: false,
        }
      },
      isLightboxVisible: false,
      lightBoxImageUrl: "",
      showDepositImg: false,
      imgsRef: null,
    };
  },
  components: {
    flatPickr,
    b2bAction,
  },
  computed: {
    ...eloadB2BComputed,
  },
  methods: {
    ...layoutMethods,
    ...exportMethods,
    ...eloadB2BMethods,
    async getRequests() {
      let params = {
        page: this.isSearching ? 1 : this.currentPage,
        rows: this.perPage,
        sort: this.sort,
        orderBy: this.orderBy,
        search: this.search,
      };

      await this.getEloadB2BRequests(params).then(() => {
        this.isSearching = false;
      });
    },
    setStatus(status) {
      return status == "requesting" ? "Requesting" : status == "approved" ? "Approved" : status == "rejected" ? "Rejected" : "";
    },
    async sortList(action) {
      var column = "";
      switch (action.sortBy) {
        case "name":
          column = "u.name";
          break;
        case "mobile_number":
          column = "u.mobile_number";
          break;
        case "business_name":
          column = "bi.name";
          break;
        default:
          column = `ebr.${action.sortBy}`
          break;
      }

      this.orderBy = column;
      this.sort = action.sortDesc ? "DESC" : "ASC";
      await this.getRequests();
    },
    async onExport() {
      let params = {
        module: "eload_b2b_requests",
        email: JSON.parse(localStorage.getItem("userData")).email,
        search: this.search,
        sort: this.sort,
        orderBy: this.orderBy,
      };

      this.exportReportExt(params)
        .then(() => Swal.fire("Success!", `Your request for report is pending. It will be sent to your email once it's ready.`, "success"))
        .catch(() => Swal.fire("Failed!", `Your request for reports has failed.`, "warning"));
    },
    viewImage(img){
        this.lightBoxImageUrl = img
        this.isLightboxVisible = true;
    }
  },
  async mounted() {
    this.changeTitle({ title: "Eload B2B Requests" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "eLoad B2B", to: { name: "" } },
        { text: "Request", to: { name: "" } },
      ],
    });

    await this.getRequests();
    await getModuleAccess('Products', 'eLoad B2B', 'Request')
      .then((access) => this.userAccess.access = access);
  },
};
</script>

<style scoped>
.requesting-color-code {
  color: #dc7512 !important;
}

.img-thumbnail {
    max-height: 50px !important;
}
</style>
